'use client'

import { ArrowRightIcon } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { motion, useReducedMotion } from 'motion/react'

import { IMAGE_CARD_IMAGE, IMAGE_CARD_SCRIM, IMAGE_CARD_SHELL } from '@/components/blocks/image-card-effect'
import { resolveImageSrc } from '@/lib/image-src'
import { SECTION_PADDING, SITE_CONTAINER } from '@/components/layout/container'
import { cn } from '@/lib/utils'

export type BentoSize = 'feature' | 'wide' | 'small'

export type BentoItem = {
  image?: string
  category?: string
  title?: string
  text?: string
  href?: string

  /** Админаас ил сонгосон хэмжээ. Хоосон бол дарааллаараа автоматаар тогтоно. */
  size?: BentoSize
}

/** Хэмжээ бүрийн сүлжээн дэх байрлал */
const SIZE_CLASS: Record<BentoSize, string> = {
  feature: 'min-h-50 sm:col-span-2 lg:col-span-1 lg:row-span-2 lg:min-h-155',
  wide: 'min-h-55 lg:min-h-0 sm:col-span-2',
  small: 'min-h-55 lg:min-h-0'
}

/**
 * Зурган дээр бичигтэй bento блок.
 *
 * Хэрэглэгчийн заасан жишиг: company.namyangi.com-ийн `main-crs-area`.
 * Тэндээс авсан гурван зарчим:
 *   1. БҮХ бичиг зургийн ДОТОР байна — гадна талд гарчиг давхарлахгүй.
 *      Тиймээс энэ блок section-ийн гарчгийг тусад нь ГАРГАХГҮЙ; эхний мөр
 *      өөрөө том карт болж гарчгийн үүргийг гүйцэтгэнэ.
 *   2. Карт бүхэлдээ линк. Хулгана дээр очиход «Дэлгэрэнгүй» гэсэн дохио
 *      бичгийн доороос ургаж гарна (namyangi: `opacity 0→1` + `margin-top 0→30px`).
 *   3. Гүйлгэхэд доороос дээш мандах (fade-up), карт бүр бага зэрэг хожимдоно.
 *
 * Байрлал (namyangi-ийн 468 + 948 харьцаа):
 *   lg: 3 багана x 2 мөр — эхний карт зүүн талд бүтэн өндрөөр, үлдсэн нь баруун талд.
 *   Сондгой тоотой үед СҮҮЛИЙН карт хоёр багана эзэлж мөрөө дүүргэнэ
 *   (namyangi-ийн доод өргөн карттай ижил), тэгснээр сүлжээнд нүх үлдэхгүй.
 */
export const PortfolioBento = ({ items, moreLabel }: { items: BentoItem[]; moreLabel: string }) => {
  const reduce = useReducedMotion()

  if (items.length === 0) return null

  /*
   * Хэмжээ: админ ил сонгосон бол ТЭР нь давамгайлна. Сонгоогүй үед хуучин
   * дүрэм хэвээр — эхний мөр том, сондгой үлдсэн үед сүүлийнх өргөн.
   */
  const sizeOf = (item: BentoItem, index: number): BentoSize => {
    if (item.size) return item.size
    if (index === 0) return 'feature'

    return (items.length - 1) % 2 === 1 && index === items.length - 1 ? 'wide' : 'small'
  }

  /** Гүйлгэхэд мандах хөдөлгөөн. Хөдөлгөөн багасгасан үед зөвхөн харагдана. */
  const reveal = (index: number) => ({
    initial: reduce ? { opacity: 1 } : { opacity: 0, y: 56 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: reduce ? { duration: 0 } : { duration: 0.5, delay: index * 0.1, ease: 'easeOut' as const }
  })

  return (
    <section className={cn(SECTION_PADDING, 'bg-muted')}>
      <div className={cn(SITE_CONTAINER, 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2')}>
        {items.map((item, index) => {
          const size = sizeOf(item, index)

          return (
            <BentoCard
              key={`${item.title}-${index}`}
              item={item}
              size={size}
              moreLabel={moreLabel}
              motionProps={reveal(index)}
              className={SIZE_CLASS[size]}
            />
          )
        })}
      </div>
    </section>
  )
}

const BentoCard = ({
  item,
  size,
  moreLabel,
  className,
  motionProps
}: {
  item: BentoItem
  size: BentoSize
  moreLabel: string
  className?: string
  motionProps: Record<string, unknown>
}) => {
  // Өргөн карт нь namyangi шиг зүүн доод буланд бичигтэй, бусад нь голлоно.
  const alignLeft = size === 'wide'

  /**
   * namyangi-ийн дохио: эхэндээ харагдахгүй, hover үед бичгийн доороос зай авч
   * ургана. Мөр голлож байрладаг тул энэ зай нэмэгдэхэд дээрх гарчиг дээшээ
   * зөөгдөнө — тэр нь эффектийн гол цөм.
   *
   * Хүрэлцэх төхөөрөмж дээр hover гэж байхгүй тул lg-ээс доош үргэлж харагдана.
   */
  const revealClass =
    'mt-2 inline-flex items-center gap-1.5 text-xl font-semibold transition-[opacity,margin] duration-700 ease-out motion-reduce:transition-none lg:mt-0 lg:opacity-0 lg:group-hover:mt-7 lg:group-hover:opacity-100'

  const body = (
    <>
      {item.image ? (
        <Image
          src={resolveImageSrc(item.image) ?? '/images/logo.svg'}
          alt=''
          fill
          sizes={
            size === 'small'
              ? '(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw'
              : '(min-width: 1024px) 50vw, 100vw'
          }
          className={IMAGE_CARD_IMAGE}
        />
      ) : (
        <div className='absolute inset-0 bg-linear-to-br from-slate-700 to-slate-900' />
      )}

      {/*
        Бүрхүүл. Зураг нь янз бүрийн тод/бараан байдаг тул цагаан бичгийн
        уншигдац зөвхөн зурагнаас хамаарах ёсгүй. `section-hero`-той ижил түвшин.
      */}
      <div className={IMAGE_CARD_SCRIM} />
      {alignLeft && <div className='absolute inset-0 bg-linear-to-t from-black/50 to-transparent' />}

      <div
        className={cn(
          'relative flex h-full flex-col justify-center gap-2 p-6 text-white sm:p-8 lg:p-12',
          alignLeft ? 'items-start text-left lg:justify-end' : 'items-center text-center'
        )}
      >
        {item.category && <span className='text-xl font-semibold tracking-wide text-white/85'>{item.category}</span>}

        {item.title && (
          <h3
            className={cn(
              'font-bold tracking-tight text-balance',
              size === 'feature' ? 'text-3xl lg:text-4xl' : 'text-2xl'
            )}
          >
            {item.title}
          </h3>
        )}

        {item.text && (
          <p className={cn('text-pretty text-white/85 text-xl')}>
            {item.text}
          </p>
        )}

        {/* Карт бүхэлдээ линк учир дэлгэц уншигчид давхардуулж уншуулахгүй. */}
        {item.href && (
          <span aria-hidden className={revealClass}>
            {moreLabel}
            <ArrowRightIcon className='size-4' />
          </span>
        )}
      </div>
    </>
  )

  const shell = cn(IMAGE_CARD_SHELL, 'rounded-3xl', className)

  // Холбоосгүй мөр нь линк болох ёсгүй — дарах зүйлгүй мөртөө курсор өөрчлөгдвөл хуурамч.
  return item.href ? (
    <motion.div {...motionProps} className={shell}>
      <Link href={item.href} className='absolute inset-0 z-10' aria-label={item.title ?? moreLabel} />
      {body}
    </motion.div>
  ) : (
    <motion.div {...motionProps} className={shell}>
      {body}
    </motion.div>
  )
}
